'use client'

import Popover from '@/components/design-system/popover'
import CloseIcon from '@/components/icons/close'
import GiftIcon from '@/components/icons/gift'
import Button from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import { Text } from '@/components/ui/text'
import useRewards from '@/hooks/use-rewards'
import { Divider, Flex, Stack } from '@/styled-system/jsx'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import BonusDeposit from './bonus-deposit'
import DailyItem from './header-rewards-dropdown-daily-item'
import Item from './header-rewards-dropdown-item'
import HeaderRewardsTrigger from './header-rewards-trigger'

export default function HeaderRewardsDropdown() {
  const t = useTranslations('header.rewards-dropdown')
  const {
    userCalendarTodayRewardAmount,
    userRakeBackAmount,
    userDailyBonusAmount,
    userWeeklyBonusAmount,
    userMonthlyBonusAmount,
    canClaimDailyBonus,
    canClaimWeeklyBonus,
    canClaimMonthlyBonus,
    canClaimCalendarTodayReward,
  } = useRewards()

  const availableRewardsCount = useMemo(() => {
    let availableRewardCount = 0

    if (userCalendarTodayRewardAmount >= 0.01) {
      availableRewardCount++
    }

    if (userRakeBackAmount >= 0.01) {
      availableRewardCount++
    }

    if (canClaimDailyBonus && userDailyBonusAmount >= 0.01) {
      availableRewardCount++
    }

    if (canClaimWeeklyBonus && userWeeklyBonusAmount >= 0.01) {
      availableRewardCount++
    }

    if (canClaimMonthlyBonus && userMonthlyBonusAmount >= 0.01) {
      availableRewardCount++
    }

    return availableRewardCount
  }, [
    canClaimDailyBonus,
    canClaimMonthlyBonus,
    canClaimWeeklyBonus,
    userCalendarTodayRewardAmount,
    userDailyBonusAmount,
    userMonthlyBonusAmount,
    userRakeBackAmount,
    userWeeklyBonusAmount,
  ])

  const [open, setOpen] = useState(false)

  const onClose = () => {
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <HeaderRewardsTrigger availableRewardsCount={availableRewardsCount} />
      }
      renderContent={
        <Flex direction='column' p={4}>
          <Flex
            alignItems='center'
            borderBottom='1px solid #251929'
            display='flex'
            pb='5'
          >
            <GiftIcon size='lg' color='primary' mr='3' />
            <Text as='span' color='white' textStyle='md' fontWeight='bold'>
              {t('title')}
            </Text>

            <IconButton variant='outline' size='xs' ml='auto' onClick={onClose}>
              <CloseIcon size='lg' color='primary' />
            </IconButton>
          </Flex>

          <Stack>
            {canClaimCalendarTodayReward ? (
              <>
                <DailyItem />
                <Divider color='#2E2232' />
              </>
            ) : null}

            <BonusDeposit />

            <Item
              type='rakeback'
              title={t('rakeback-item.title')}
              reward={userRakeBackAmount}
              canClaim={true}
              onClaim={onClose}
            />

            <Item
              type='daily'
              title={t('daily-item.title')}
              reward={userDailyBonusAmount}
              canClaim={canClaimDailyBonus}
              onClaim={onClose}
            />

            <Item
              type='weekly'
              title={t('weekly-item.title')}
              reward={userWeeklyBonusAmount}
              canClaim={canClaimWeeklyBonus}
              onClaim={onClose}
            />

            <Item
              type='monthly'
              title={t('monthly-item.title')}
              reward={userMonthlyBonusAmount}
              canClaim={canClaimMonthlyBonus}
              onClaim={onClose}
            />

            <Button
              variant='tertiary'
              size='md'
              border='1px solid #251929'
              h='12'
              w='full'
              href='/profile/rewards'
              onClick={onClose}
            >
              {t('redirect-button')}
            </Button>
          </Stack>
        </Flex>
      }
    />
  )
}
